/**
 * ModalController - Generic modal dialog management
 * 
 * Provides utilities for opening, closing, and managing modal dialogs.
 * Handles backdrop clicks, ESC key, and focus management.
 */

// Registry of active modals (stack for nested modals)
const modalStack = [];

// Map of modal IDs to their close handlers
const closeHandlers = new Map();

// Set of modals currently being closed (prevents recursion)
const closingModals = new Set();

/**
 * Register a modal with its close handler
 * @param {string} modalId - DOM ID of the modal element
 * @param {Function} closeHandler - Function to call when modal should close
 */
export function registerModal(modalId, closeHandler) {
    closeHandlers.set(modalId, closeHandler);
}

/**
 * Open a modal by ID
 * @param {string} modalId - DOM ID of the modal element
 * @param {Object} options - Optional configuration
 * @param {boolean} options.focusFirst - Focus first focusable element (default: true)
 * @param {string} options.focusSelector - Custom selector for element to focus
 */
export function openModal(modalId, options = {}) {
    const modal = document.getElementById(modalId);
    if (!modal) {
        console.warn(`Modal not found: ${modalId}`);
        return;
    }
    
    // Add to stack
    modalStack.push(modalId);
    
    // Activate modal
    modal.classList.add('active');
    
    // Focus management
    if (options.focusFirst !== false) {
        const focusTarget = options.focusSelector 
            ? modal.querySelector(options.focusSelector)
            : modal.querySelector('input, button, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusTarget) {
            setTimeout(() => focusTarget.focus(), 50);
        }
    }
}

/**
 * Close a specific modal by ID
 * @param {string} modalId - DOM ID of the modal element
 */
export function closeModal(modalId) {
    // Guard against recursive calls (cleanup() calls closeModal which would call handler again)
    if (closingModals.has(modalId)) {
        return;
    }
    
    const modal = document.getElementById(modalId);
    if (!modal) return;
    
    // Mark as closing to prevent recursion
    closingModals.add(modalId);
    
    // Remove from stack
    const idx = modalStack.indexOf(modalId);
    if (idx > -1) {
        modalStack.splice(idx, 1);
    }
    
    // Deactivate modal
    modal.classList.remove('active');
    
    // Call registered close handler if any
    const handler = closeHandlers.get(modalId);
    if (handler) {
        handler();
    }
    
    // Clear closing state
    closingModals.delete(modalId);
}

/**
 * Close the topmost modal (for ESC key handling)
 */
export function closeTopModal() {
    if (modalStack.length > 0) {
        const topModalId = modalStack[modalStack.length - 1];
        closeModal(topModalId);
        return true;
    }
    return false;
}

/**
 * Check if any modal is currently open
 * @returns {boolean}
 */
export function hasOpenModal() {
    return modalStack.length > 0;
}

/**
 * Get the currently active modal ID
 * @returns {string|null}
 */
export function getActiveModal() {
    return modalStack.length > 0 ? modalStack[modalStack.length - 1] : null;
}

/**
 * Check if a specific modal is open
 * @param {string} modalId - DOM ID of the modal element
 * @returns {boolean}
 */
export function isModalOpen(modalId) {
    return modalStack.includes(modalId);
}

/**
 * Initialize modal controller - sets up global event handlers
 * Call this once on app startup
 */
export function initModalController() {
    // ESC key closes topmost modal (registered or unregistered)
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            // First try registered modals
            if (hasOpenModal()) {
                e.preventDefault();
                closeTopModal();
                return;
            }
            
            // Fallback: close any active modal-overlay (for modals not using registration)
            const activeOverlays = document.querySelectorAll('.modal-overlay.active');
            if (activeOverlays.length > 0) {
                e.preventDefault();
                // Close the last one (topmost)
                const topOverlay = activeOverlays[activeOverlays.length - 1];
                topOverlay.classList.remove('active');
            }
        }
    });
    
    // Click on backdrop closes modal
    document.addEventListener('click', (e) => {
        // Check if click was on a modal backdrop (dialog-modal structure)
        const backdrop = e.target.closest('.modal-backdrop');
        if (backdrop && e.target === backdrop) {
            const modal = backdrop.closest('.modal');
            if (modal && modal.id) {
                closeModal(modal.id);
            }
            return;
        }
        
        // Check if click was directly on a modal-overlay (not its content)
        if (e.target.classList.contains('modal-overlay') && e.target.classList.contains('active')) {
            e.target.classList.remove('active');
        }
    });
}

/**
 * Create a simple confirmation modal
 * @param {Object} options - Configuration
 * @param {string} options.title - Modal title
 * @param {string} options.message - Modal message
 * @param {string} options.confirmText - Confirm button text (default: 'OK')
 * @param {string} options.cancelText - Cancel button text (default: 'Cancel')
 * @param {boolean} options.danger - Style as danger action
 * @returns {Promise<boolean>} Resolves true if confirmed, false if cancelled
 */
export function showConfirmModal({ 
    title = 'Confirm', 
    message = 'Are you sure?', 
    confirmText = 'OK',
    cancelText = 'Cancel',
    danger = false
}) {
    return new Promise((resolve) => {
        // Create modal elements
        const modalId = 'confirm-modal-' + Date.now();
        const modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'modal dialog-modal';
        modal.innerHTML = `
            <div class="modal-backdrop">
                <div class="modal-content dialog-modal-content">
                    <h3>${escapeHtmlBasic(title)}</h3>
                    <p class="dialog-message">${escapeHtmlBasic(message)}</p>
                    <div class="modal-actions">
                        <button class="btn-cancel">${escapeHtmlBasic(cancelText)}</button>
                        <button class="btn-confirm ${danger ? 'danger' : ''}">${escapeHtmlBasic(confirmText)}</button>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        const cleanup = (result) => {
            closeHandlers.delete(modalId);  // Remove handler FIRST to prevent recursion
            closeModal(modalId);
            modal.remove();
            resolve(result);
        };
        
        // Event handlers
        modal.querySelector('.btn-cancel').addEventListener('click', () => cleanup(false));
        modal.querySelector('.btn-confirm').addEventListener('click', () => cleanup(true));
        
        // Register for ESC key handling (backdrop click handled by closeModal)
        registerModal(modalId, () => cleanup(false));
        openModal(modalId);
    });
}

/**
 * Create a simple alert modal (just OK button)
 * @param {Object} options - Configuration
 * @param {string} options.title - Modal title (default: 'Alert')
 * @param {string} options.message - Modal message
 * @param {string} options.confirmText - Confirm button text (default: 'OK')
 * @returns {Promise<void>} Resolves when dismissed
 */
export function showAlertModal({
    title = 'Alert',
    message = '',
    confirmText = 'OK'
}) {
    return new Promise((resolve) => {
        const modalId = 'alert-modal-' + Date.now();
        const modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'modal dialog-modal';
        modal.innerHTML = `
            <div class="modal-backdrop">
                <div class="modal-content dialog-modal-content">
                    <h3>${escapeHtmlBasic(title)}</h3>
                    <p class="dialog-message">${escapeHtmlBasic(message)}</p>
                    <div class="modal-actions">
                        <button class="btn-confirm">${escapeHtmlBasic(confirmText)}</button>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        const cleanup = () => {
            closeHandlers.delete(modalId);  // Remove handler FIRST to prevent recursion
            closeModal(modalId);
            modal.remove();
            resolve();
        };
        
        modal.querySelector('.btn-confirm').addEventListener('click', cleanup);
        
        // Register for ESC key handling
        registerModal(modalId, cleanup);
        openModal(modalId);
    });
}

/**
 * Create a simple prompt modal
 * @param {Object} options - Configuration
 * @param {string} options.title - Modal title
 * @param {string} options.message - Modal message/label
 * @param {string} options.placeholder - Input placeholder
 * @param {string} options.defaultValue - Default input value
 * @param {string} options.confirmText - Confirm button text (default: 'OK')
 * @param {string} options.cancelText - Cancel button text (default: 'Cancel')
 * @returns {Promise<string|null>} Resolves with input value or null if cancelled
 */
export function showPromptModal({
    title = 'Enter value',
    message = '',
    placeholder = '',
    defaultValue = '',
    confirmText = 'OK',
    cancelText = 'Cancel'
}) {
    return new Promise((resolve) => {
        const modalId = 'prompt-modal-' + Date.now();
        const modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'modal dialog-modal';
        modal.innerHTML = `
            <div class="modal-backdrop">
                <div class="modal-content dialog-modal-content">
                    <h3>${escapeHtmlBasic(title)}</h3>
                    ${message ? `<p class="dialog-message">${escapeHtmlBasic(message)}</p>` : ''}
                    <input type="text" class="dialog-input" placeholder="${escapeHtmlBasic(placeholder)}" value="${escapeHtmlBasic(defaultValue)}">
                    <div class="modal-actions">
                        <button class="btn-cancel">${escapeHtmlBasic(cancelText)}</button>
                        <button class="btn-confirm">${escapeHtmlBasic(confirmText)}</button>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        const input = modal.querySelector('.dialog-input');
        
        const cleanup = (result) => {
            closeHandlers.delete(modalId);  // Remove handler FIRST to prevent recursion
            closeModal(modalId);
            modal.remove();
            resolve(result);
        };
        
        // Event handlers
        modal.querySelector('.btn-cancel').addEventListener('click', () => cleanup(null));
        modal.querySelector('.btn-confirm').addEventListener('click', () => cleanup(input.value));
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                cleanup(input.value);
            }
        });
        
        // Register for ESC key handling
        registerModal(modalId, () => cleanup(null));
        openModal(modalId, { focusSelector: '.dialog-input' });
        
        // Select all text in input
        input.select();
    });
}

// Basic HTML escape (inline to avoid import dependencies)
function escapeHtmlBasic(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
}
